import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { supabase, apiFetch } from '@/lib/supabaseClient';

const AuthCtx = createContext(null);

/**
 * AuthContext: wraps Supabase Auth session and backend /api/me scope.
 * Coexists with the legacy AppContext (Ferie V1 in-memory data).
 *
 * CRITICAL: Supabase-js v2 acquires a navigator LockManager lock inside
 * onAuthStateChange callbacks. If we await ANY supabase.auth.* call inside
 * that callback (e.g. via refreshMe -> apiFetch -> getSession()), we deadlock.
 * Rule: schedule refreshMe with setTimeout(0) inside onAuthStateChange.
 * See: https://github.com/supabase/supabase-js/issues/992
 */
export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const inFlight = useRef(null);

  const refreshMe = useCallback(async () => {
    if (inFlight.current) return inFlight.current;
    const request = (async () => {
      try {
        const data = await apiFetch('/api/me');
        setMe(data);
        setError(null);
        return data;
      } catch (e) {
        setMe(null);
        setError(e.message || 'Errore caricamento profilo');
        return null;
      }
    })();
    inFlight.current = request;
    try {
      return await request;
    } finally {
      if (inFlight.current === request) inFlight.current = null;
    }
  }, []);

  // Initial session load + auth state subscription.
  useEffect(() => {
    let mounted = true;
    // Safety net: guarantee loading resolves even if getSession hangs.
    const failsafe = setTimeout(() => {
      if (mounted) setLoading(false);
    }, 6000);

    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!mounted) return;
        setSession(data.session);
        if (data.session) await refreshMe();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[Auth] initial getSession failed:', e);
      } finally {
        if (mounted) {
          setLoading(false);
          clearTimeout(failsafe);
        }
      }
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      // MUST NOT await inside the callback (supabase-js lock).
      setSession(s);
      setTimeout(() => {
        if (!mounted) return;
        if (s) refreshMe();
        else setMe(null);
      }, 0);
    });

    return () => {
      mounted = false;
      clearTimeout(failsafe);
      sub.subscription.unsubscribe();
    };
  }, [refreshMe]);

  const signIn = useCallback(async (email, password) => {
    const { data, error: err } = await supabase.auth.signInWithPassword({ email, password });
    if (err) throw err;
    setSession(data.session);
    const meData = await refreshMe();
    if (!meData) {
      throw new Error('Accesso riuscito, ma il profilo autorizzato non è disponibile. Riprova o contatta l’amministratore.');
    }
    return { ...data, me: meData };
  }, [refreshMe]);

  const signOut = useCallback(async () => {
    if (me?.is_global) {
      try {
        await apiFetch('/api/ceo/tenant-context', { method: 'DELETE' });
      } catch (_) {
        // An expired CEO session has no context to clear.
      }
    }
    await supabase.auth.signOut();
    setSession(null);
    setMe(null);
  }, [me]);

  const value = {
    session,
    isAuthenticated: !!session,
    me,
    loading,
    error,
    signIn,
    signOut,
    refreshMe,
  };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth deve essere usato dentro AuthProvider');
  return ctx;
};

/**
 * Maps a Foundation role_code to the legacy AppContext role id (Ferie V1).
 * Foundation → Legacy mapping. Used only to bridge the Login to Ferie V1 UI.
 */
export function mapRoleCodeToLegacyId(roleCode) {
  const map = {
    CEO: 'ceo',
    TITOLARE: 'titolare',
    RESP_GEN: 'resp_generale',
    RESP_LOCALE: 'resp_locale',
    SEGRETERIA: 'segretaria',
    DIPENDENTE: 'dipendente',
  };
  return map[roleCode] || null;
}
